<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      One of the first things that need to be done when a sick animal arrives at the vet’s is to
      take the animal’s vital signs before deciding on the next course of action to take.
    </p>
    <p class="mb-5">
      Sometimes too it may be necessary to instruct pet owners on how to perform these checks at
      home so they can tell when it’s time to pay a visit to the vet.
    </p>
    <p class="mb-5">The basic vital signs to check are:</p>
    <ul class="default-list">
      <li>Capillary refill time</li>
      <li>Heart rate</li>
      <li>Respiratory rate</li>
      <li>Temperature.</li>
    </ul>
    <p class="mb-5">Let’s take a look at how to measure each one.</p>
    <h4 class="mb-5">Checking Capillary Refill Time (CRT)</h4>
    <p class="mb-5">
      CRT is a measurement that checks the rate of blood flow in blood vessels called the
      capillaries. This is done by pressing on the animal’s gums with your fingers to force the
      blood out of the capillaries. When you force the blood out like this, the gums will turn
      white.
    </p>
    <p class="mb-5">
      Now, when you remove your finger, time how long it takes for the capillaries to refill. This
      is the time it takes for the gums to regain their red colour. For healthy dogs and cats this
      should be less than 2 seconds.
    </p>
    <h4 class="mb-5">Heart Rate</h4>
    <p class="mb-5">
      The heart rate should be taken while the animal is resting. Different animals have different
      healthy heart rates, some of which can be seen in the image below.
    </p>
    <p class="mb-5">
      The best place to check an animal’s heart rate is on the femoral artery. This can be found on
      the inside of the thigh near the groin.
    </p>
    <p class="mb-5">
      To take the heart rate, press down lightly on the femoral and count the pulses or beats. Heart
      rate is measured in beats per minute (bpm). One quick way to test the heart rate is to count
      how many beats you feel in 15 seconds and multiply this by 4 - this will give you a rough
      measurement of beats per minute.
    </p>
    <h4 class="mb-5">Respiratory Rate</h4>
    <p class="mb-5">
      The respiratory rate means the number of breaths an animal takes per minute. Breathing at too
      fast or too slow a rate can indicate a health problem.
    </p>
    <p class="mb-5">
      Also, while a dog at rest will usually breathe through their nose, they can breathe through
      their mouths too. Cats, on the other hand, only breathe through their mouths when they are in
      ill health.
    </p>
    <p class="mb-5">
      To check an animal’s respiratory rate, observe their chest and count the number of breaths you
      see in one minute. Like the heart rate, for a quick version of this count the number of
      visible breaths for 15 seconds and multiply by 4.
    </p>
    <h4 class="mb-5">Body Temperature</h4>
    <p class="mb-5">
      As with humans, an animal’s body temperature can be a useful indicator of their current state
      of health.
    </p>
    <p class="mb-5">
      Unlike humans, however, we can’t take a dog or cat’s temperature by sticking a thermometer
      under their arm or tongue. For these animals, temperature is taken rectally. There are various
      healthy ranges for different types of animals, you can find some of these in the following
      document (click the image below).
    </p>
    <p class="mb-5"><i>Reference Ranges for Pet Vital Signs:</i></p>

    <centered-image-wrapper
      classes="cursor-pointer"
      :image="image1"
      label="View the Reference Ranges for Pet Vital Signs document"
      @clicked="showIframePopup(popupVideo)"
    />

    <p class="mb-5">
      Now, watch the following video and learn from a vet how to check a pet’s vital signs:
    </p>
    <responsive-youtube-wrapper :video="videos[1]" :image="videoTrigger1" />
    <p class="mb-5">
      Once you’ve gained a good idea of how to check a pet’s vital signs, can you check the
      following vital signs of a cat or dog?
    </p>
    <ul class="default-list">
      <li>Capillary Refill Rate</li>
      <li>Heart Rate</li>
      <li>Respiratory Rate</li>
    </ul>
    <p class="mb-5">DO NOT TAKE THEIR TEMPERATURE for this task.</p>

    <p class="mb-5">Record the type of subject and the results in the table below:</p>

    <table class="table default-table">
      <tr class="header">
        <th width="25%">Subject</th>
        <th width="25%">CRT Result</th>
        <th width="25%">Heart Rate</th>
        <th>Respiratory Rate</th>
      </tr>
      <tr class="body" v-for="index in 3" :key="index">
        <td><text-block v-model="data['question' + index + '_1']" :editable="isEditable" /></td>
        <td><text-block v-model="data['question' + index + '_2']" :editable="isEditable" /></td>
        <td><text-block v-model="data['question' + index + '_3']" :editable="isEditable" /></td>
        <td><text-block v-model="data['question' + index + '_4']" :editable="isEditable" /></td>
      </tr>
    </table>

    <p class="mb-5">
      If you don’t have a dog or cat to check, perhaps you can check a family member’s pet if you
      feel safe with that animal and you have permission from a parent or guardian. If this is not
      an option, then just tick the box below.
    </p>
    <div class="vx-row mb-6">
      <div class="vx-col w-full">
        <vs-checkbox v-model="data['question5']" name="confirmed" :disabled="!isEditable"
          >I have read, listened and learned this task.</vs-checkbox
        >
      </div>
    </div>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      image1: require('@/assets/images/experiences/vet/vital_signs_button.jpg'),
      videoTrigger1: require('@/assets/images/experiences/vet/activity_42.jpg'),
      videos: {
        1: 'https://www.youtube.com/embed/Lv63ZUTp_IM',
      },
      login_type: localStorage.getItem('login_type'),
    };
  },
  popupVideo: $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/vet/pet_vital_signs.jpg',
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.scale-header {
  background: #f5f5f5;
  padding-top: 5px;
  font-weight: 600;
}
</style>
